import PropTypes from 'prop-types';
import * as R from 'ramda';
import React, { useEffect, useState } from 'react';
import { Col, Row, Spinner } from 'react-bootstrap';
import { useDispatch, useStore } from 'react-redux';
import { lookerParams } from '../../../../../shared/lookerParams';
import { errorHandler } from '../../../../../shared/utils';
import { updateLookerTabsVisited } from '../../../../../stores/actions/lookerActions';
import VistaToggleButton from '../../../../Common/VistaToggleButton';
import { getModelEmbedUrl } from '../modelOutputService';

const propTypes = {
    /**
     * ActiveTab is provided by the Tabs component.
     */
    activeTab: PropTypes.string,
    /**
     * The tabLabel is the same label provided to the Tabs component.
     */
    tabLabel: PropTypes.string,
    /**
     * The function to fire once the iframes have loaded
     */
    onLoadComplete: PropTypes.func,
    /**
     * By default, we only get a "Table" look. This adds the "Chart" and the toggle.
     */
    includeChart: PropTypes.bool,
    /**
     * The templates to use when fetching new iframe URLs.
     */
    lookerTemplates: PropTypes.shape({
        chart: PropTypes.string,
        table: PropTypes.string,
    }),
};

const defaultProps = {
    onLoadComplete: Function,
    includeChart: false,
    lookerTemplates: {},
};

function OutputTab({ tabLabel, includeChart, onLoadComplete, activeTab, lookerTemplates }) {
    //#region State
    const [isPending, setIsPending] = useState(true);
    const [tableUrl, setTableUrl] = useState('');
    const [chartUrl, setChartUrl] = useState('');
    const [showChart, setShowChart] = useState(false);

    const { modelChangeAction, lookerTabsVisited } = useStore().getState();

    const lookerTabLabel = lookerTabsVisited[tabLabel];
    const dispatch = useDispatch();

    const { modelVersionId, customerId } = R.pathOr(
        {
            modelVersionId: null,
            customerId: null,
        },
        ['modelPayload', 'modelDetails'],
        modelChangeAction,
    );
    //#endregion

    //#region Click/Action/Event Handlers
    const loadComplete = () => {
        dispatch(updateLookerTabsVisited({ [tabLabel]: true }));
        onLoadComplete();
    };
    //#endregion

    //#region Side Effects
    useEffect(() => {
        if (modelVersionId && activeTab === tabLabel && !lookerTabLabel) {
            getModelEmbedUrl({
                lookerPaths: lookerTemplates,
                lookerModel: lookerParams.lookerPath.models,
                customerId,
                modelVersionId,
                filters: {},
            })
                .then((res) => {
                    if (res.status === 'Success') {
                        setTableUrl(res.data.table);
                        setChartUrl(res.data.chart);
                    }
                })
                .catch(errorHandler)
                .finally(() => {
                    setIsPending(false);
                });
        } else {
            setIsPending(false);
        }
    }, [activeTab, lookerTabLabel, modelVersionId, tabLabel, lookerTemplates, customerId]);

    //#endregion

    //#region RunTime Calculations
    //#endregion

    return (
        <div className="tab-box">
            <Row>
                <Col md={12}>
                    <div className="iframe-graph">
                        {isPending ? <Spinner animation="border" role="status" data-testid="spinner" /> : null}

                        {(!isPending && includeChart && chartUrl && activeTab === tabLabel) || lookerTabLabel ? (
                            <iframe
                                className={showChart ? 'd-block' : 'd-none'}
                                onLoad={loadComplete}
                                id="looker"
                                title="looker-graph"
                                scrolling="no"
                                src={chartUrl}
                                allowFullScreen
                                data-testid="chart-iframe"
                            />
                        ) : null}

                        {(!isPending && tableUrl && activeTab === tabLabel) || lookerTabLabel ? (
                            <iframe
                                className={showChart ? 'd-none' : 'd-block'}
                                onLoad={loadComplete}
                                id="looker"
                                title="looker-graph"
                                scrolling="no"
                                src={tableUrl}
                                allowFullScreen
                                data-testid="table-iframe"
                            />
                        ) : null}
                    </div>
                </Col>
            </Row>

            {includeChart ? (
                <div className="iframe-toggle">
                    <Row>
                        <VistaToggleButton
                            showChart={showChart}
                            onToggle={(value) => {
                                setShowChart(!value);
                            }}
                        />
                    </Row>
                </div>
            ) : null}
        </div>
    );
}

OutputTab.propTypes = propTypes;
OutputTab.defaultProps = defaultProps;

export default OutputTab;
